import { useState, useCallback, useEffect, useRef } from 'react';
import { MicroLearningAPI } from '../apis/micro-learning.api';
import { useAtom } from 'jotai';
import { profileAtom } from '../components/Settings/atom';

interface UseLearningTutorialReturn {
  hasCompletedTutorial: boolean;
  isLoading: boolean;
  markTutorialComplete: () => Promise<void>;
  resetTutorial: () => Promise<void>;
}

/**
 * Hook to manage the learning system tutorial completion status
 * Backend is the single source of truth
 */
export const useLearningTutorial = (): UseLearningTutorialReturn => {
  const [profile] = useAtom(profileAtom);
  const [hasCompletedTutorial, setHasCompletedTutorial] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const hasLoadedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  // Load completion status from backend
  useEffect(() => {
    if (!profile?.id) {
      setIsLoading(false);
      return;
    }

    // Prevent re-loading for same user (but allow for different users)
    if (hasLoadedRef.current && lastUserIdRef.current === profile.id) return;
    hasLoadedRef.current = true;
    lastUserIdRef.current = profile.id;

    const loadTutorialStatus = async () => {
      setIsLoading(true);
      try {
        const status = await MicroLearningAPI.getTutorialStatus();
        setHasCompletedTutorial(status.completed);
      } catch (error) {
        console.warn('[Tutorial] Failed to load status:', error);
        setHasCompletedTutorial(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadTutorialStatus();
  }, [profile?.id]);

  const markTutorialComplete = useCallback(async () => {
    try {
      await MicroLearningAPI.completeTutorial();
      setHasCompletedTutorial(true);
    } catch (error) {
      console.warn('[Tutorial] Failed to mark complete:', error);
    }
  }, []);

  const resetTutorial = useCallback(async () => {
    try {
      await MicroLearningAPI.resetTutorial();
      setHasCompletedTutorial(false);
    } catch (error) {
      console.warn('[Tutorial] Failed to reset:', error);
    }
  }, []);

  return {
    hasCompletedTutorial,
    isLoading,
    markTutorialComplete,
    resetTutorial,
  };
};

export default useLearningTutorial;
